import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import { api } from '../../services/api-client';

interface IWorkoutTemplate {
  id: number;
  planName: string;
}

interface IWorkoutOption {
  label: string;
  value: number;
}

interface ICycleOption {
  label: string;
  value: number;
  weeks?: number;
}

interface IAssignment {
  workoutId: number | null;
  dayOfWeek: number | null;
}

interface IExistingByWeek {
  [weekNumber: number]: { workoutId: number; dayOfWeek: number }[];
}

interface IVerificationResult {
  hasSetLogs: boolean;
  setLogsCount: number;
}

interface IUseAssignWorkoutToCycleDialogProps {
  visible: boolean;
  onHide: () => void;
  clientId: number | string;
  setRefreshKey: (fn: (old: number) => number) => void;
  cycleOptions: ICycleOption[];
  actionType: 'assign' | 'unassign';
}

export function useAssignWorkoutToCycleDialog({
  visible,
  onHide,
  clientId,
  setRefreshKey,
  cycleOptions,
  actionType
}: IUseAssignWorkoutToCycleDialogProps) {
  const intl = useIntl();
  const { showToast } = useToast();
  const { coach } = useUser();

  const [workouts, setWorkouts] = useState<IWorkoutTemplate[]>([]);
  const [assignments, setAssignments] = useState<IAssignment[]>([{ workoutId: null, dayOfWeek: null }]);
  const [cycle, setCycle] = useState(-1);
  const [cycles, setCycles] = useState<ICycleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [assignedWorkouts, setAssignedWorkouts] = useState<IWorkoutOption[]>([]);
  const [unassignOption, setUnassignOption] = useState<'day' | 'cycle'>('day');
  const [existingByWeek, setExistingByWeek] = useState<IExistingByWeek>({});
  const [verificationResult, setVerificationResult] = useState<IVerificationResult | null>(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  // Replicar la asignación a todas las semanas del ciclo (default) o solo a la
  // semana que el coach está viendo en la grilla (cuando se destilda).
  const [replicate, setReplicate] = useState(true);

  const daysOfWeek = [
    { label: intl.formatMessage({ id: 'workoutTable.monday' }), value: 1 },
    { label: intl.formatMessage({ id: 'workoutTable.tuesday' }), value: 2 },
    { label: intl.formatMessage({ id: 'workoutTable.wednesday' }), value: 3 },
    { label: intl.formatMessage({ id: 'workoutTable.thursday' }), value: 4 },
    { label: intl.formatMessage({ id: 'workoutTable.friday' }), value: 5 },
    { label: intl.formatMessage({ id: 'workoutTable.saturday' }), value: 6 },
    { label: intl.formatMessage({ id: 'workoutTable.sunday' }), value: 7 }
  ];

  useEffect(() => {
    if (visible) {
      setAssignments([{ workoutId: null, dayOfWeek: null }]);
      setCycle(-1);
      setSelectedDay(null);
      setAssignedWorkouts([]);
      setUnassignOption('day');
      setVerificationResult(null);
      setForceDelete(false);
      setShowVerificationDialog(false);
      setReplicate(true);
    }
  }, [visible]);

  useEffect(() => {
    const loadWorkouts = async () => {
      try {
        const { data } = await api.workout.findAllWorkoutTemplatesByCoachId();
        setWorkouts(data ?? []);
      } catch (error) {
        showToast('error', 'Error', (error as Error).message);
      }
    };

    if (visible) loadWorkouts();
  }, [showToast, coach?.id, visible]); // eslint-disable-line

  useEffect(() => {
    if (cycleOptions && visible) {
      setCycles(cycleOptions);
    }
  }, [cycleOptions, visible]);

  // When in assign mode and a cycle is selected, fetch the cycle so the grid
  // can show what's already assigned per week. Existing assignments are
  // displayed as locked pills (no X) — the coach only submits NEW drops.
  useEffect(() => {
    const loadExisting = async () => {
      if (actionType !== 'assign' || cycle === -1 || !visible) {
        setExistingByWeek({});
        return;
      }
      try {
        const { data } = await api.workout.fetchTrainingCyclesByClient(Number(clientId));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const target = (data as any[] | undefined)?.find((c) => c.id === cycle);
        if (!target) {
          setExistingByWeek({});
          return;
        }
        const map: IExistingByWeek = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (target.trainingWeeks ?? []).forEach((week: any) => {
          const list: { workoutId: number; dayOfWeek: number }[] = [];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (week.trainingSessions ?? []).forEach((s: any) => {
            const day = Number(s.dayNumber);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (s.workoutInstances ?? []).forEach((wi: any) => {
              const workoutId = Number(wi?.workoutTemplate?.id ?? wi?.workoutTemplateId);
              if (Number.isFinite(workoutId) && Number.isFinite(day)) {
                list.push({ workoutId, dayOfWeek: day });
              }
            });
          });
          map[Number(week.weekNumber)] = list;
        });
        setExistingByWeek(map);
      } catch (error) {
        showToast('error', 'Error', (error as Error).message);
      }
    };
    loadExisting();
  }, [actionType, cycle, visible, clientId, showToast]);

  useEffect(() => {
    const loadAssignedWorkouts = async () => {
      if (actionType === 'unassign' && cycle !== -1 && selectedDay !== null && unassignOption === 'day') {
        try {
          const { data } = await api.workout.fetchAssignedWorkoutsForCycleDay(cycle, selectedDay);
          setAssignedWorkouts(
            (data ?? []).map((workout: { planName: string; id: number }) => ({
              label: workout.planName,
              value: workout.id
            }))
          );
        } catch (error) {
          showToast('error', 'Error fetching assigned workouts', (error as Error).message);
        }
      }
    };

    loadAssignedWorkouts();
  }, [showToast, actionType, cycle, selectedDay, unassignOption]);

  const handleVerifyDeletion = async () => {
    if (cycle === -1) return showToast('error', 'Error', 'Please select a cycle.');

    try {
      setLoading(true);
      const { data } = await api.workout.verifyTrainingCycleDeletion(cycle);
      setVerificationResult(data);
      setShowVerificationDialog(true);
    } catch (error) {
      showToast('error', 'Error', (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (weekNumber?: number) => {
    if (cycle === -1) return showToast('error', 'Error', 'Please select a cycle.');

    try {
      setLoading(true);
      if (actionType === 'assign') {
        const body = {
          assignments: assignments.filter(
            (assignment) => assignment.dayOfWeek !== null && assignment.workoutId !== null
          ),
          // Si replicate está destildado, la asignación va solo a la semana visible.
          replicate,
          ...(replicate ? {} : { weekNumber })
        };
        if (body.assignments.length === 0) return showToast('error', 'Error', 'Please select at least one workout.');

        const { data } = await api.workout.assignWorkoutsToCycle(cycle, Number(clientId), body);
        if (data && data.trainingSessions && data.trainingSessions.length > 0) {
          showToast(
            'success',
            intl.formatMessage({ id: 'assignWorkoutToCycleDialog.success.assign' }),
            intl.formatMessage({ id: 'assignWorkoutToCycleDialog.success.assign.detail' })
          );
        } else {
          showToast(
            'error',
            intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.assign' }),
            intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.assign.detail' })
          );
        }
      } else {
        if (unassignOption === 'day') {
          if (selectedDay === null) {
            return showToast('error', 'Error', 'Please select a day of the week.');
          }

          const body = {
            assignments: assignments.filter(
              (assignment) => assignment.dayOfWeek !== null && assignment.workoutId !== null
            )
          };

          if (body.assignments.length === 0) {
            return showToast('error', 'Error', 'Please select at least one workout to unassign.');
          }

          const { message } = await api.workout.unassignWorkoutsFromCycle(cycle, body);
          if (message === 'success') {
            showToast(
              'success',
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.success.unassign' }),
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.success.unassign.detail' })
            );
          } else {
            showToast(
              'error',
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.unassign' }),
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.unassign.detail' })
            );
          }
        } else {
          if (!showVerificationDialog) {
            await handleVerifyDeletion();
            return;
          }

          const { message } = await api.workout.deleteTrainingCycle(cycle, forceDelete);
          if (message === 'success') {
            showToast(
              'success',
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.success.deleteCycle' }),
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.success.deleteCycle.detail' })
            );
            setShowVerificationDialog(false);
            onHide();
            setSelectedDay(null);
            setRefreshKey((old) => old + 1);
          } else {
            showToast(
              'error',
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.deleteCycle' }),
              intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.deleteCycle.detail' })
            );
          }
        }
      }
      if (actionType === 'assign' || unassignOption === 'day') {
        onHide();
        setSelectedDay(null);
        setRefreshKey((old) => old + 1);
      }
    } catch (error) {
      showToast('error', 'Error', (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssignment = () => {
    const last = assignments[assignments.length - 1];
    if (!last || !last.workoutId || last.dayOfWeek === null) {
      return showToast(
        'error',
        'Error',
        intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.selectWorkoutAndDay' })
      );
    }
    setAssignments([...assignments, { workoutId: null, dayOfWeek: null }]);
  };

  const handleAssignmentChange = (index: number, field: keyof IAssignment, value: number | null) => {
    setAssignments((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const removeAssignment = (index: number) => {
    const updated = assignments.filter((_, i) => index !== i);
    if (updated.length > 0) {
      setAssignments(updated);
    } else {
      showToast(
        'error',
        'Error',
        intl.formatMessage({ id: 'assignWorkoutToCycleDialog.error.selectAtLeastOneWorkout' })
      );
    }
  };

  /** Drag&drop entry point — set/replace the assignment for a given day. */
  const setAssignmentForDay = (dayOfWeek: number, workoutId: number) => {
    setAssignments((prev) => {
      const next = prev.filter((a) => a.dayOfWeek !== dayOfWeek);
      const emptyIdx = next.findIndex((a) => a.workoutId == null && a.dayOfWeek == null);
      const entry: IAssignment = { workoutId, dayOfWeek };
      if (emptyIdx >= 0) {
        return next.map((a, i) => (i === emptyIdx ? entry : a));
      }
      return [...next, entry];
    });
  };

  /** Drag&drop entry point — clear assignments on a given day. */
  const clearAssignmentForDay = (dayOfWeek: number) => {
    setAssignments((prev) => {
      const filtered = prev.filter((a) => a.dayOfWeek !== dayOfWeek);
      return filtered.length === 0 ? [{ workoutId: null, dayOfWeek: null }] : filtered;
    });
  };

  const handleCycleChange = (value: number) => {
    setCycle(value);
    if (actionType === 'unassign') {
      setSelectedDay(null);
      setAssignedWorkouts([]);
    }
  };

  const handleDayChange = (value: number) => {
    setSelectedDay(value);
    setAssignments([{ workoutId: null, dayOfWeek: value }]);
  };

  return {
    intl,
    workouts,
    assignments,
    cycle,
    cycles,
    loading,
    selectedDay,
    assignedWorkouts,
    unassignOption,
    verificationResult,
    forceDelete,
    showVerificationDialog,
    daysOfWeek,
    setUnassignOption,
    setForceDelete,
    setShowVerificationDialog,
    handleAction,
    handleAddAssignment,
    handleAssignmentChange,
    removeAssignment,
    setAssignmentForDay,
    clearAssignmentForDay,
    existingByWeek,
    handleCycleChange,
    handleDayChange,
    replicate,
    setReplicate
  };
}
